import React from 'react';

export const traumaInformedPalettes = {
  default: {
    name: "Default",
    description: "Standard dark theme",
    background: "#3e3e3e",
    tileBg: "#1e1e1e",
    tileHeaderBg: "#1e1e1e",
    text: "#ffffff",
    accent: "#4caf50",
    buttonBg: "#4caf50",
    secondaryBg: "#2e2e2e",
    inputBg: "#575656", // Existing input background
    borderColor: "#444"
  },
   monochrome: {
    name: "Monochrome Green",
    description: "Calming green grayscale for minimal visual stimulation",
    background: "#1a2421", // Dark green-black
    tileBg: "#121a16",    // Darker green-black
    tileHeaderBg: "#121a16",
    text: "lime",      // Very light green-white
    accent: "#2e7d32",    // Medium green
    buttonBg: "#1b5e20",  // Dark green
    secondaryBg: "#1e2d1e", // Dark green-black
    inputBg: "#0d1a0d",   // Almost black with green undertone
    borderColor: "#1b5e20"
  },
  calm: {
    name: "Calm Blue",
    description: "Soothing blue tones for reduced anxiety",
    background: "#2c3e50",
    tileBg: "#1a2530",
    tileHeaderBg: "#1a2530",
    text: "#ecf0f1",
    accent: "#3498db",
    buttonBg: "#3498db",
    secondaryBg: "#34495e",
    inputBg: "#476481",
    borderColor: "#34495e"
  },
  gentle: {
    name: "Gentle Earth",
    description: "Soft earth tones for grounding",
    background: "#5d4037",
    tileBg: "#3e2723",
    tileHeaderBg: "#3e2723",
    text: "#f5f5f5",
    accent: "#8d6e63",
    buttonBg: "#8d6e63",
    secondaryBg: "#4e342e",
    inputBg: "#6d4c41",
    borderColor: "#4e342e"
  },
  muted: {
    name: "Muted Pastel",
    description: "Low-contrast colors to reduce visual stress",
    background: "#455a64",
    tileBg: "#263238",
    tileHeaderBg: "#263238",
    text: "#eceff1",
    accent: "#78909c",
    buttonBg: "#78909c",
    secondaryBg: "#37474f",
    inputBg: "#546e7a",
    borderColor: "#37474f"
  },
  soft: {
    name: "Soft Contrast",
    description: "Minimal contrast for visual comfort",
    background: "#37474f",
    tileBg: "#263238",
    tileHeaderBg: "#263238",
    text: "#cfd8dc",
    accent: "#607d8b",
    buttonBg: "#607d8b", 
    secondaryBg: "#2e3c43",
    inputBg: "#455a64",
    borderColor: "#2e3c43"
  },
  lavender: {
    name: "Lavender Calm",
    description: "Soft purple tones for emotional soothing",
    background: "#4a4064",
    tileBg: "#352f4a",
    tileHeaderBg: "#352f4a",
    text: "#e6e6fa",
    accent: "#8a6fbd",
    buttonBg: "#8a6fbd",
    secondaryBg: "#443d5b",
    inputBg: "#5a5272",
    borderColor: "#443d5b"
  },
  sunset: {
    name: "Warm Sunset",
    description: "Soft warm tones for emotional warmth",
    background: "#5d4037",
    tileBg: "#3e2723",
    tileHeaderBg: "#3e2723",
    text: "#ffd54f",
    accent: "#ff9800",
    buttonBg: "#ff9800",
    secondaryBg: "#4e342e",
    inputBg: "#6d4c41",
    borderColor: "#4e342e"
  }
};

export function generateMonochromeColors() {
  return {
  textColors: {
      default: "#c8e6c9",     // Light sage green (main text)
      heading: "#e8f5e9",     // Very light mint green (headings)
      subtle: "#a5d6a7",      // Muted green (subtle text)
      disabled: "#81c784"     // Lighter green (disabled/secondary text)
    },
    distressTriggerColors: {
      "😄": "#4caf50",    // Lightest green
      "🙂": "#388e3c",    // Slightly darker green
      "😐": "#2e7d32",    // Medium green
      "😟": "#1b5e20",    // Dark green
      "😢": "#1b5e20"     // Darkest green
    },
    distressColors: {
      1: "#4caf50",      // Lowest distress - lightest green
      2: "#43a047",
      3: "#388e3c",
      4: "#2e7d32",
      5: "#1e5626",
      6: "#1b5e20",
      7: "#165c1c",
      8: "#125418",
      9: "#0d4012",
      10: "#0a330e",     // Highest distress - darkest green
      null: "#0a330e"
    },
    journalColors: {
      Happy: "#4caf50",            // Lightest green
      "Slightly happy": "#2e7d32", // Medium green
      Worried: "#1b5e20",          // Dark green
      "Very distressed": "#0d4012" // Darkest green
    }
  };
};

const PaletteSelector = ({ selectedPalette, onSelect }) => {
  // Additional verbose logging
  console.error('PaletteSelector Rendering:', {
    selectedPalette, 
    onSelect: onSelect ? 'Function provided' : 'No function',
    availablePalettes: Object.keys(traumaInformedPalettes)
  });

  // Inline styles to ensure visibility
  const containerStyle = {
    color: 'white',
    fontSize: '10px'
  };

  const optionStyle = {
    display: 'flex',
    alignItems: 'center',
    margin: '0px 0',
    padding: '5px',
    cursor: 'pointer',
    border: '1px solid transparent',
    borderRadius: '4px'
  };

  const activeOptionStyle = {
    ...optionStyle,
    border: '1px solid #4caf50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)'
  };

  return (
    <div style={containerStyle}>
      
      <p style={{ margin: '0 0 10px 0', color: '#aaa' }}>
        Select a color theme that feels comfortable for you
      </p>
      
      {/* Render each palette option */}
      {Object.entries(traumaInformedPalettes).map(([key, palette]) => (
        <div 
          key={key}
          style={selectedPalette === key ? activeOptionStyle : optionStyle}
          onClick={() => {
            console.error(`Attempting to select palette: ${key}`);
            if (onSelect) {
              onSelect(key);
            } else {
              console.error('No onSelect function provided');
            }
          }}
        >
          <div 
            style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: palette.background, 
              marginRight: '10px',
              border: `2px solid ${palette.accent}`
            }} 
          />
          <div>
            <div style={{ fontWeight: 'bold', color: 'white' }}>
              {palette.name}
            </div>
            <div style={{ color: '#aaa', fontSize: '10px' }}>
              {palette.description}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PaletteSelector;